import { MemberListContent } from '@app/member/list/MemberListContent';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import {
    MemberId,
    MemberLight,
    MemberSummary,
    MissionSessionLight,
    MissionSummary,
    MissionSessionId,
} from '@wwf/sdk';
import { Box, Stack, Typography } from '@mui/material';
import { BulkActionsToolbar } from 'ra-ui-materialui';
import {
    ListContextProvider,
    ResourceContextProvider,
    useGetMany,
    useGetOne,
    useList,
    useListContext,
} from 'react-admin';
import { useMemo, type ReactNode } from 'react';
import { MissionParticipantAddForm } from './MissionParticipantAddForm';
import { MissionParticipantBulkDeleteButton } from './MissionParticipantBulkDeleteButton';

const MissionParticipantBulkDeleteWrapper = ({
    sessionId,
}: {
    sessionId: MissionSessionId;
}) => {
    const { selectedIds, onUnselectItems } = useListContext();

    if (!selectedIds || selectedIds.length === 0) {
        return null;
    }

    return (
        <MissionParticipantBulkDeleteButton
            selectedIds={selectedIds}
            onUnselectItems={onUnselectItems}
            sessionId={sessionId}
        />
    );
};

export const MissionSessionParticipantPanel = ({
    record,
    session,
}: {
    record: MissionSummary;
    session: MissionSessionLight;
}) => {
    const { data: members, isPending } = useGetMany<MemberSummary>(
        'member',
        { ids: session.participantIds ?? [] },
        { enabled: (session.participantIds?.length ?? 0) > 0 }
    );

    const sessionContactId = session.contactId;
    const defaultMissionContactId = (record.contact as MemberLight | null)?.id;

    const membersExcludingSessionContact = useMemo(() => {
        const list = members ?? [];
        const excludeId = sessionContactId ?? defaultMissionContactId;
        if (!excludeId) return list;
        return list.filter(m => m.id !== excludeId);
    }, [members, sessionContactId, defaultMissionContactId]);

    const listContext = useList({
        data: membersExcludingSessionContact,
        isPending,
        resource: 'member',
        sort: { field: 'firstName', order: 'ASC' },
        perPage: 1000,
    });
    const { data: sessionContactMember, isPending: isSessionContactPending } =
        useGetOne<MemberSummary>(
            'member',
            { id: sessionContactId as MemberId },
            { enabled: !!sessionContactId }
        );

    let sessionContactValue: ReactNode;
    if (sessionContactId) {
        if (isSessionContactPending) {
            sessionContactValue = (
                <Typography variant="body2" color="textSecondary">
                    …
                </Typography>
            );
        } else if (sessionContactMember) {
            sessionContactValue = (
                <MemberLightCard record={sessionContactMember} />
            );
        } else {
            sessionContactValue = (
                <Typography variant="body2" color="textSecondary">
                    —
                </Typography>
            );
        }
    } else if (record.contact) {
        sessionContactValue = (
            <Stack gap={0.5}>
                <MemberLightCard
                    record={record.contact as MemberLight | MemberSummary}
                />
                <Typography variant="caption" color="textSecondary">
                    Mission contact (default)
                </Typography>
            </Stack>
        );
    } else {
        sessionContactValue = (
            <Typography variant="body2" color="textSecondary">
                —
            </Typography>
        );
    }

    return (
        <Box mt={2}>
            <Box mb={2}>
                <LabelValueRow
                    label="Session contact"
                    value={sessionContactValue}
                />
            </Box>
            <MissionParticipantAddForm
                record={record}
                sessionId={session.id}
                participantIds={session.participantIds}
            />

            {(session.participantIds?.length ?? 0) > 0 ? (
                <ResourceContextProvider value="member">
                    <ListContextProvider value={listContext}>
                        <BulkActionsToolbar>
                            <MissionParticipantBulkDeleteWrapper
                                sessionId={session.id}
                            />
                        </BulkActionsToolbar>

                        <MemberListContent />
                    </ListContextProvider>
                </ResourceContextProvider>
            ) : (
                <Box mt={2}>
                    <Typography color="textSecondary">
                        No participants
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
